/**
 * 1102-FE4 · 「切换企业」在途旧响应不覆盖新页面 — 请求层租户戳 + 按租户清理注册表。
 *
 * 设计（报告 §3 选择：**戳丢弃**，而非统一 abort）：
 * - 发请求时把当时的 effectiveTenantId 盖在 config 上（见 request.ts 请求拦截器）；
 * - 响应回来时，若戳 ≠ 当前租户 ⇒ 视为「过期响应」，直接静默丢弃
 *   （不进 store、不渲染页面、不弹 ElMessage）；
 * - 之所以选戳丢弃而不是 AbortController 统一 abort：
 *   ① 业务 store 全是组件本地 ref、没有集中 store 可遍历 abort；
 *   ② 切租户时在途请求大多已经过半，abort 会让用户看到 loading 错误闪烁；
 *   ③ 戳丢弃是「响应层单点拦截」，改动面最小，且不破坏 axios 既有 401/刷新/队列逻辑。
 *
 * 注意：后端逐请求的租户/对象范围校验仍保留不动——这里是前端兜底，不是替代门禁。
 *
 * 本模块**严禁** import vue / pinia / vue-router / axios / element-plus —— 它要被
 * scripts/test-tenant-switch-stale-response.ts 这类纯 Node 脚本直接 import，
 * 一旦牵进任何 window/document 依赖，脚本就会在模块加载期崩掉。
 */

/** 切租户时被静默丢弃的过期响应所拒绝的专用错误 */
export class StaleTenantResponseError extends Error {
  constructor(public stampedTenantId: string | null, public currentTenantId: string | null) {
    super(`stale tenant response: stamped=${stampedTenantId ?? '(null)'} current=${currentTenantId ?? '(null)'}`)
    this.name = 'StaleTenantResponseError'
  }
}

/** 判断这次响应是否属于「切租户前的在途响应，现在已经过期」。 */
export function isStaleTenantResponse(stampedTenantId: string | null, currentTenantId: string | null): boolean {
  // 任意一边为 null（未登录/未选企业）就不比——否则刚登出/未初始化的正常响应会被误伤
  if (stampedTenantId == null || currentTenantId == null) return false
  return stampedTenantId !== currentTenantId
}

/* ------------------------------------------------------------------ *
 * 集中式「按租户清理」注册表（纯模块，无 vue/pinia 依赖）。
 *
 * 业务模块在 onMounted/setup 里把自己的「清租户相关缓存」函数注册进来
 * （onUnmounted 时按句柄注销）。selectTenant 切换前由 auth store 统一
 * 调用 runTenantReset()——**不是** $reset() 整个 pinia，
 * 登录态 / 权限 / 菜单树 / 当前用户 / 租户字典（tenants）都保留。
 * ------------------------------------------------------------------ */

const tenantResetHandlers = new Set<() => void>()

/** 注册一个「按租户清理」回调；返回注销句柄（重复注册同一函数会被去重）。 */
export function registerTenantReset(handler: () => void): () => void {
  tenantResetHandlers.add(handler)
  return () => { tenantResetHandlers.delete(handler) }
}

/** 由 auth store.selectTenant 在写 selectedTenantId 之前调用：跑一遍所有已注册的清理回调。
 *  单个回调抛错不阻塞其余回调——业务侧自己的清理函数不该有副作用。 */
export function runTenantReset() {
  for (const h of tenantResetHandlers) {
    try { h() } catch { /* 单个清理失败不阻塞其余 */ }
  }
}

/** 仅供测试：把注册表清空（生产代码别调用）。 */
export function __clearTenantResetRegistryForTest() {
  tenantResetHandlers.clear()
}
